import { env } from '@/lib/env'
import { ApiError } from './errors'

let getAccessToken = () => null
let onUnauthorized = async () => false

/** Wire the auth store in without a circular import (called once from auth/store). */
export function setAccessTokenGetter(fn) {
  getAccessToken = fn
}

/**
 * Called when a request with an access token gets 401. Should try a silent refresh and
 * resolve true if a new token is available (the request is then retried once).
 */
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn
}

const RETRYABLE_401 = new Set(['TOKEN_INVALID', 'TOKEN_REVOKED'])

async function parseProblem(res) {
  const body = await res.json().catch(() => ({}))
  return new ApiError({
    status: res.status,
    code: body.code ?? 'HTTP_ERROR',
    title: body.title ?? res.statusText,
    detail: body.detail,
    errors: body.errors ?? [],
    requestId: body.request_id ?? res.headers.get('X-Request-ID'),
  })
}

/**
 * Fetch JSON from the OneMentor API.
 * @param {string} path  e.g. '/api/v1/health/live'
 * @param {object} [opts]
 * @param {import('zod').ZodType} [opts.schema]  Validates the response; a mismatch throws.
 * @param {string} [opts.method]
 * @param {unknown} [opts.body]  Serialised as JSON.
 * @param {AbortSignal} [opts.signal]
 */
export async function apiFetch(path, options = {}) {
  try {
    return await request(path, options)
  } catch (err) {
    const hadToken = Boolean(getAccessToken())
    if (
      hadToken &&
      !options._retried &&
      err.status === 401 &&
      RETRYABLE_401.has(err.code) &&
      (await onUnauthorized())
    ) {
      return request(path, { ...options, _retried: true })
    }
    throw err
  }
}

async function request(path, { schema, method = 'GET', body, form, signal, headers = {}, raw = false } = {}) {
  const token = getAccessToken()
  let res
  try {
    res = await fetch(`${env.VITE_API_URL}${path}`, {
      method,
      signal,
      credentials: 'include', // refresh-token cookie (Phase 1)
      headers: {
        Accept: 'application/json',
        ...(body !== undefined && !form && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
        'X-Requested-With': 'fetch',
        ...headers,
      },
      body: form ?? (body === undefined ? undefined : JSON.stringify(body)),
    })
  } catch (err) {
    if (err?.name === 'AbortError') throw err
    throw new ApiError(
      {
        status: 0,
        code: 'NETWORK_ERROR',
        title: 'Network error',
        detail: "Can't reach the server. Check your connection.",
      },
      { cause: err },
    )
  }

  if (!res.ok) throw await parseProblem(res)
  if (res.status === 204) return null
  if (raw) return res

  const data = await res.json()
  if (!schema) return data
  const parsed = schema.safeParse(data)
  if (!parsed.success) {
    throw new ApiError(
      {
        status: res.status,
        code: 'BAD_RESPONSE',
        title: 'Unexpected response',
        detail: `Unexpected response from ${path}`,
      },
      { cause: parsed.error },
    )
  }
  return parsed.data
}
